// @flow

import * as React from 'react';

import WindowResize from '@ta/events.window-resize';
import { Overlay, type Position } from '@ta/overlays.pieces';

/** Attachment edge */
export type Edge = 'top' | 'bottom' | 'left' | 'right' | 'windowX';
/** Attachment position */
export type Attachment = 'above' | 'below' | 'leftOf' | 'rightOf';
/** Window padding */
export const WindowPadding = 20;

/** The component props for a <AttachedOverlay> */
export type Props = {|
  /** Element to attach this overlay to. */
  target: HTMLElement,

  /** Direction relative to the selected element to display the overlay */
  position: Attachment,

  /**
   * The edge to which you want to align the target and the overlay. Can be 'top', 'bottom', 'left', 'right'. If not
   * provided (i.e. 'undefined') it will be centered. Defaults to undefined/centered.
   */
  alignEdge?: Edge,

  /** True if it is fixed position, false if it is absolute. Defaults to false/absolute. */
  fixed?: boolean,

  /** True if the overlay should animate position changes */
  animated: boolean,

  /** Child react nodes */
  children: React.Node,

  /** Choice determines the z-index of the overlay */
  overlayType?: 'modal' | 'background' | 'tooltip' | 'popover',
|};

type State = {|
  position: Position,
|};

/**
 * When we calculate a position from the right or bottom of the document body we need to flip the offset value.
 * For example if the body is 800px wide and we want to position something 100px in from the left edge we either want
 * "left: 100px" or "right: 700px" (which is the body width of 800px minus the 100px value).
 *
 * For fixed positioning, we need to pay extra special attention to the viewport height for mobile devices, but the idea is the same.
 */
const clientMap = (from: string, to: string, value: any, fixed: any) => {
  /* eslint-disable no-restricted-globals */
  const { position } = window.getComputedStyle(document.body);
  const { top, left } = (document.body && document.body.getBoundingClientRect()) || {};
  const isPositioned = position !== 'static';
  const { clientHeight = 0, clientWidth = 0 } =
    (typeof document !== 'undefined' &&
      (isPositioned ? document.body : document.documentElement)) ||
    {};
  const { innerHeight = 0, innerWidth = 0 } = window;
  /* eslint-disable no-restricted-globals */
  if (from === 'right' && (to === 'right' || to === 'left')) {
    // The asymmetry here is to handle mobile pinch-to-zoom
    if (fixed) {
      return Math.min(innerWidth, clientWidth) - value;
    }
    return clientWidth + (isPositioned ? left : 0) - value;
  } else if (from === 'bottom' && (to === 'top' || to === 'bottom')) {
    if (fixed) {
      return Math.max(clientHeight, innerHeight) - value;
    }
    return clientHeight + (isPositioned ? top : 0) - value;
  } else if (from === 'left') {
    return (isPositioned ? window.pageXOffset - left : 0) + value;
  } else if (from === 'top') {
    return (isPositioned ? window.pageYOffset - top : 0) + value;
  }
  return value;
};

/**
 * Get the initial position of the overlay relative to the target element as specified by 'selector'. Optionally
 * align edges of the target and overlay. Also maintain position fixed relative to the viewport or absolute relative
 * to the target element.
 */
export const getPosition = (
  elmt: any,
  targetEdge: string,
  overlayEdge: string,
  fixed: any,
  align: any,
): Position => {
  // get the target element bounding client rect which is relative to the viewport
  const rect = elmt.getBoundingClientRect();

  const { pageXOffset = 0, pageYOffset = 0 } = window || {};

  // If we are using absolute positioning then we need to transpose these coordinates to be relative to the document by
  // adding the scroll offset.
  const bodyRect = {
    top: rect.top + (fixed ? 0 : pageYOffset),
    left: rect.left + (fixed ? 0 : pageXOffset),
    right: rect.right + (fixed ? 0 : pageXOffset),
    bottom: rect.bottom + (fixed ? 0 : pageYOffset),
  };

  const position: Position = {
    position: fixed ? 'fixed' : 'absolute',
    [overlayEdge]: `${clientMap(overlayEdge, targetEdge, bodyRect[targetEdge], fixed)}px`,
  };

  // If we're aligning based on the width of the window, just add some padding and call it good.
  if (align && align === 'windowX') {
    position.left = `${WindowPadding}px`;
    position.right = `${WindowPadding}px`;
  }
  // if we are aligning the overlay to an edge then grab the corresponding position offset
  else if (align) {
    position[align] = `${clientMap(align, align, bodyRect[align], fixed)}px`;

    // else if we are centering it on the top or bottom edge of the target
  } else if (targetEdge === 'top' || targetEdge === 'bottom') {
    // offset the overlay so the left edge is centered on the target and then shift back 50% so the centers of each
    // are aligned
    position.left = `${bodyRect.left + rect.width / 2}px`;
    position.transform = 'translate(-50%)';

    // else if we are centering it on the left or right edge of the target
  } else {
    // offset the overlay so the left edge is centered on the target and then shift back 50% so the centers of each
    // are aligned
    position.top = `${bodyRect.top + rect.height / 2}px`;
    position.transform = 'translate(0,-50%)';
  }

  return position;
};

/**
 * Get the position of the overlay based on component properties
 */
export const getPositionForProps = (props: Props): Position => {
  if (props.position === 'above') {
    return getPosition(props.target, 'top', 'bottom', props.fixed, props.alignEdge);
  }
  if (props.position === 'leftOf') {
    return getPosition(props.target, 'left', 'right', props.fixed, props.alignEdge);
  }
  if (props.position === 'rightOf') {
    return getPosition(props.target, 'right', 'left', props.fixed, props.alignEdge);
  }
  // below
  return getPosition(props.target, 'bottom', 'top', props.fixed, props.alignEdge);
};

/**
 * Animation properties used if animation is enabled on this overlay
 */
const TRANSITION_DURATION = 250; // ms
const animationProperties = [
  {
    property: 'left',
    timing: TRANSITION_DURATION,
  },
  {
    property: 'top',
    timing: TRANSITION_DURATION,
  },
];

/**
 * An overlay that is attached / positioned relative to a specific element.
 *
 * Helpful positioning hints:
 *
 * - Note that the positioning is relative to the target element's content and doesn't include the padding, border, or
 * margin. It may be easiest to wrap your content in a div and then target that div.
 *
 * - Be aware of margin collapsing. If margins in the target's ancestry get collapsed then that could throw the
 * positioning off. See https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Box_Model/Mastering_margin_collapsing
 */
export default class AttachedOverlay extends React.Component<Props, State> {
  static defaultProps = {
    alignEdge: undefined,
    fixed: false,
    animated: false,
    overlayType: 'modal',
  };

  static getDerivedStateFromProps(props: Props) {
    if (props.position) {
      return {
        position: getPositionForProps(props),
      };
    }
    return null;
  }

  constructor(props: Props) {
    super(props);
    this.state = { position: getPositionForProps(props) };
  }

  // For this function code coverage was disabled since it's almost impossible simulate window resize
  setPosition = /* istanbul ignore next */ () => {
    this.setState({
      position: getPositionForProps(this.props),
    });
  };

  render() {
    if (this.state && this.state.position !== null) {
      return (
        <Overlay
          position={this.state.position}
          transitionProperties={this.props.animated ? animationProperties : []}
          overlayType={this.props.overlayType}
        >
          {this.props.children}
          <WindowResize callback={this.setPosition} throttle={100} />
        </Overlay>
      );
    }
    return null;
  }
}
